import { useEffect } from 'react';

import { pickBy } from 'lodash-es';
import queryString from 'query-string';
import {
  Navigate,
  Outlet,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import AbsencesPage from '@/features/absences/components/AbsencesPage';
import AnnouncementDetailPage from '@/features/announcements/components/AnnouncementDetailPage';
import AnnouncementsIndexPage from '@/features/announcements/components/AnnouncementsIndexPage';
import ContactPage from '@/features/contact/components/ContactPage';
import ForgotPage from '@/features/forgot/components/ForgotPage';
import InvitationRegistration from '@/features/invitations/components/InvitationRegistration';
import LoginPage from '@/features/login/components/LoginPage';
import EmailChangeCompletePage from '@/features/mypage/components/EmailChangeCompletePage';
import EmailChangePage from '@/features/mypage/components/EmailChangePage';
import EmailChangeSentPage from '@/features/mypage/components/EmailChangeSentPage';
import InviteRoute from '@/features/mypage/components/invite/InviteRoute';
import OrganizationMembership from '@/features/mypage/components/OrganizationMembership';
import ParentNameChangePage from '@/features/mypage/components/ParentNameChangePage';
import ParentPasswordChangePage from '@/features/mypage/components/ParentPasswordChangePage';
import ParentProfilePageV2 from '@/features/mypage/components/ParentProfilePageV2';
import NoticeListPage from '@/features/notice/components/NoticeListPage';
import NoticeShowPage from '@/features/notice/components/NoticeShowPage';
import PasswordChangeEmailConfirmationPage from '@/features/password/components/PasswordChangeEmailConfirmationPage';
import ResetPassword from '@/features/password/components/ResetPassword';
import AccountRegistration from '@/features/registration/components/AccountRegistration';
import AccountRegistrationCompletePage from '@/features/registration/components/AccountRegistrationCompletePage';
import ShinroIndexPage from '@/features/shinro/components/ShinroIndexPage';
import WithdrawalPage from '@/features/withdrawal/components/WithdrawalPage';
import useAuthentication from '@/hooks/common/useAuthentication';
import useHomePath from '@/hooks/common/useHomePath';
import useModal from '@/hooks/common/useModal';
import useAppDownloadPopupDismissal from '@/hooks/mypage/useAppDownloadPopupDismissal';
import useCurrentUser from '@/hooks/store/useCurrentUser';

export const Authenticated: React.FC = () => {
  const { isLoggedIn } = useAuthentication();
  const location = useLocation();
  const [currentParent] = useCurrentUser();
  const { showModal } = useModal();
  const { data: appDownloadPopupDismissal } = useAppDownloadPopupDismissal({
    enabled: Boolean(currentParent),
  });

  useEffect(() => {
    if (appDownloadPopupDismissal && !appDownloadPopupDismissal.dismissed) {
      showModal('AppDownloadPopupModal', {});
    }
  }, [appDownloadPopupDismissal, showModal]);

  if (isLoggedIn) {
    return <Outlet />;
  } else {
    // utm_*のクエリパラメータを保持する
    const queryParams = queryString.parse(location.search);
    const utmParams = pickBy(queryParams, (_, k) => k.startsWith('utm_'));

    return (
      <Navigate
        replace
        to={{
          pathname: '/login',
          search: queryString.stringify(utmParams),
        }}
        state={{
          redirectTo: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }
};

const Unauthenticated: React.FC<{ fallback?: React.ReactNode }> = ({
  fallback = <ErrorPage type="not_found" />,
}) => {
  const { isLoggedIn } = useAuthentication();

  if (isLoggedIn) {
    return fallback;
  } else {
    return <Outlet />;
  }
};

const Routes: React.FC = () => {
  const homePath = useHomePath();

  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate replace to={homePath} />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/notices/:noticeId" element={<NoticeShowPage />} />
      <Route path="/notices" element={<NoticeListPage />} />
      <Route
        path="/email_change_tokens/:token"
        element={<EmailChangeCompletePage />}
      />
      <Route path="/invitations/*" element={<InvitationRegistration />} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route
        path="/forgot/password/confirmation"
        element={<PasswordChangeEmailConfirmationPage />}
      />
      <Route path="/password/reset/*" element={<ResetPassword />} />

      <Route element={<Authenticated />}>
        <Route path="/announcements" element={<AnnouncementsIndexPage />} />
        <Route
          path="/announcements/:announcementId"
          element={<AnnouncementDetailPage />}
        />
        <Route path="/shinro" element={<ShinroIndexPage />} />
        <Route path="/absences/*" element={<AbsencesPage />} />
        <Route path="/mypage/profile" element={<ParentProfilePageV2 />} />
        <Route
          path="/mypage/profile/name/change"
          element={<ParentNameChangePage />}
        />
        <Route
          path="/mypage/profile/email/change"
          element={<EmailChangePage />}
        />
        <Route
          path="/mypage/profile/email/change/sent"
          element={<EmailChangeSentPage />}
        />
        <Route
          path="/mypage/profile/password/change"
          element={<ParentPasswordChangePage />}
        />
        <Route
          path="/mypage/organization_memberships/*"
          element={<OrganizationMembership />}
        />
        <Route path="/mypage/invite/*" element={<InviteRoute />} />
        <Route path="/withdrawal" element={<WithdrawalPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Route>
      {/* verify 画面でログインしてから遷移してくるので、Unauthenticated の外に置く */}
      <Route
        path="/registration/complete"
        element={<AccountRegistrationCompletePage />}
      />
      <Route
        element={
          <Unauthenticated
            fallback={<Navigate replace to="/mypage/profile" />}
          />
        }
      >
        <Route path="/registration/*" element={<AccountRegistration />} />
      </Route>

      <Route path="*" element={<ErrorPage type="not_found" />} />
    </RouterRoutes>
  );
};

export default Routes;
